import { WatchQueryFetchPolicy } from '@apollo/client';
import {
  TaskFilterInputObject,
  useSitesPerformanceMetricsByTaskDeadlinesMissedQuery,
  useSitesPerformanceMetricsByTaskResultsQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { StatusKind } from 'routes/Dashboard/Monitoring/enums';

type Args = {
  additionalFilters?: TaskFilterInputObject[];
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
  ids?: string[];
  statusKind?: StatusKind;
};

export default function useSitesPerformanceMetrics({
  additionalFilters,
  skip,
  fetchPolicy = 'network-only',
  ids = [],
  statusKind,
}: Args) {
  const filters: TaskFilterInputObject[] = [];
  if (additionalFilters?.length) filters.push(...additionalFilters);

  const baseOptions = {
    fetchPolicy,
    variables: { ids, taskFilters: filters },
  } as const;

  const missed = useSitesPerformanceMetricsByTaskDeadlinesMissedQuery({
    ...baseOptions,
    skip: !!skip || statusKind !== StatusKind.DeadlineMissed,
  });
  const results = useSitesPerformanceMetricsByTaskResultsQuery({
    ...baseOptions,
    skip: !!skip || statusKind === StatusKind.DeadlineMissed,
  });

  const active = statusKind === StatusKind.DeadlineMissed ? missed : results;
  const { loading, error, refetch, previousData, data } = active as any;
  const queryData = data || previousData;
  const firstLoading = loading && previousData === undefined;
  const raw = _get(queryData, 'data', []) as any[];
  const dataCollection = Array.isArray(raw)
    ? raw.map((d) => ({
        id: d.id,
        totalAnomalies:
          statusKind === StatusKind.DeadlineMissed
            ? d.totalAnomaliesByTaskDeadlinesMissed
            : d.totalAnomaliesByTaskResults,
        successPercentage:
          statusKind === StatusKind.DeadlineMissed
            ? d.successPercentageByTaskDeadlinesMissed
            : d.successPercentageByTaskResults,
      }))
    : raw;

  return {
    data: dataCollection,
    error,
    firstLoading,
    loading,
    refetch,
  };
}
